import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useLang, LANGS } from "@/lib/i18n";

export default function LanguageSwitcher({ dark = false }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="lang-switcher-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border border-current/25 px-3.5 py-1.5 font-mono-alt text-[11px] uppercase tracking-[0.2em] opacity-90 transition-opacity hover:opacity-100"
        aria-label="Cambia lingua / Change language"
      >
        <Globe className="h-3.5 w-3.5" />
        {lang.toUpperCase()}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="lang-switcher-menu"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className={`absolute right-0 top-full z-[65] mt-2 w-40 overflow-hidden rounded-2xl border shadow-xl ${
              dark ? "border-ivory/20 bg-night text-ivory" : "border-bordeaux/15 bg-white text-ink"
            }`}
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
                  dark ? "hover:bg-ivory/10" : "hover:bg-bordeaux/5"
                } ${lang === l.code ? "font-semibold" : ""}`}
              >
                <span>
                  <span className="mr-2 font-mono-alt text-[10px] uppercase tracking-[0.15em] opacity-60">
                    {l.code.toUpperCase()}
                  </span>
                  {l.name}
                </span>
                {lang === l.code && <Check className="h-3.5 w-3.5 text-bordeaux-light" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
